/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@material-ui/core";
import EqualizerIcon from "@material-ui/icons/Equalizer";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import PermDataSettingIcon from "@material-ui/icons/PermDataSetting";

// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

// Gráficos (conservados, no eliminados)
// import ChartComparationLast from "./ultimos/chartBarUltimo.js";
// import ChartComparationAcum from "./acumulador/chartBar.js";
// import ChartComparationInd from "./indicadores/chartBar.js";
// import ChartComparationCos from "./costos/chartBar.js";

import { api } from "../../../services/api";
import { useAuth } from "../../../contexts/AuthContext";

// ── Estado labels ──────────────────────────────────────────────────
const ESTADO_REQ_OC = {
  1: { label: "Borrador",          color: "#f59e0b" },
  2: { label: "Enviado a aprobar", color: "#3b82f6" },
};

const ESTADO_OS = {
  1: { label: "Pendiente ejecución",          color: "#f59e0b" },
  2: { label: "En proceso",                   color: "#3b82f6" },
  5: { label: "Pendiente transferencia",       color: "#8b5cf6" },
  6: { label: "Pendiente aprobación Director", color: "#ec4899" },
  7: { label: "Pendiente envío cliente",       color: "#06b6d4" },
};

const EstadoChip = ({ estado, mapa }) => {
  const info = mapa[estado] || { label: `Estado ${estado}`, color: "#9ca3af" };
  return (
    <Chip
      label={info.label}
      size="small"
      style={{
        backgroundColor: info.color,
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.7rem",
      }}
    />
  );
};

// ── Estilos ────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  cardTitleWhite: {
    color: "#fff",
    fontWeight: 700,
    fontSize: "1rem",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  cardSubtitle: {
    color: "rgba(255,255,255,0.8)",
    fontSize: "0.78rem",
    marginTop: 2,
  },
  tableHeader: {
    "& th": {
      fontWeight: 700,
      fontSize: "0.78rem",
      padding: "8px 10px",
      backgroundColor: "#f1f5f9",
      borderBottom: "2px solid #e2e8f0",
    },
  },
  tableRow: {
    "&:hover": { backgroundColor: "#f8fafc" },
    "& td": {
      fontSize: "0.8rem",
      padding: "6px 10px",
    },
  },
  emptyMsg: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: "#94a3b8",
    fontSize: "0.85rem",
  },
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.25)",
    color: "#fff",
    borderRadius: 12,
    padding: "2px 8px",
    fontSize: "0.75rem",
    fontWeight: 700,
    marginLeft: 8,
  },
}));

// ── Componente de lista genérica ───────────────────────────────────
function PendingList({ title, icon: Icon, color, columns, rows, loading }) {
  const classes = useStyles();
  return (
    <Card>
      <CardHeader plain style={{ padding: "14px 16px", background: "#000033", borderRadius: "4px 4px 0 0", margin: 0 }}>
        <div className={classes.cardTitleWhite}>
          <Icon fontSize="small" />
          {title}
          <span className={classes.badge}>{rows.length}</span>
        </div>
        <p className={classes.cardSubtitle}>Registros pendientes activos</p>
      </CardHeader>
      <CardBody style={{ padding: 0 }}>
        {loading ? (
          <div className={classes.loadingWrapper}>
            <CircularProgress size={28} />
          </div>
        ) : rows.length === 0 ? (
          <p className={classes.emptyMsg}>No hay registros pendientes.</p>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow className={classes.tableHeader}>
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align || "left"}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={i} className={classes.tableRow}>
                    {columns.map((col) => (
                      <TableCell key={col.key} align={col.align || "left"}>
                        {col.render ? col.render(row) : row[col.key] ?? "—"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardBody>
    </Card>
  );
}

// ── Helpers ────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return "—";
  const s = typeof d === "string" ? d.split("T")[0] : String(d);
  const [y, m, day] = s.split("-");
  return `${day}/${m}/${y}`;
};

// ── Componente principal ───────────────────────────────────────────
export default function ChartDyn() {
  const { getUser } = useAuth();
  const user = getUser();
  // Normalizar sectores: usar array de sectores o fallback a [IdSector]
  const userSectores = Array.isArray(user?.sectores) && user.sectores.length > 0
    ? user.sectores.map(Number)
    : user?.IdSector ? [Number(user.IdSector)] : [];
  // const isAdmin = user?.es_admin === 1;

  const [requisiciones, setRequisiciones] = useState([]);
  const [ordenesCompra, setOrdenesCompra] = useState([]);
  const [ordenesServicio, setOrdenesServicio] = useState([]);
  const [loadingReq, setLoadingReq] = useState(true);
  const [loadingOC, setLoadingOC] = useState(true);
  const [loadingOS, setLoadingOS] = useState(true);

  // ESTADOS EXCLUIDOS: Cerrado/Aprobado (3) y Rechazado/Cancelado (4)
  const EXCLUIDOS_REQ_OC = [3, 4];
  const EXCLUIDOS_OS = [3, 4];

  useEffect(() => {
    // Requisiciones
    api.requisiciones.getAll()
      .then((data) => {
        const filtradas = data
          .filter((r) => !r.requisionOculto)
          .filter((r) => !EXCLUIDOS_REQ_OC.includes(r.IdEstado))
          // .filter((r) => isAdmin || r.IdSector === user?.IdSector);
          .filter((r) => userSectores.includes(Number(r.IdSector)));
        setRequisiciones(filtradas);
      })
      .catch(console.error)
      .finally(() => setLoadingReq(false));

    // Órdenes de Compra
    api.ordenesCompra.getAll()
      .then((data) => {
        const filtradas = data
          .filter((r) => !r.oculto)
          .filter((r) => !EXCLUIDOS_REQ_OC.includes(r.IdEstado))
          // .filter((r) => isAdmin || r.IdSector === user?.IdSector);
          .filter((r) => userSectores.includes(Number(r.IdSector)));
        setOrdenesCompra(filtradas);
      })
      .catch(console.error)
      .finally(() => setLoadingOC(false));

    // Órdenes de Servicio
    api.ordenesServicios.getAll()
      .then((data) => {
        const filtradas = data
          .filter((r) => !r.oculto)
          .filter((r) => !EXCLUIDOS_OS.includes(r.estadoOS))
          // .filter((r) => isAdmin || r.idSector === user?.IdSector);
          .filter((r) => userSectores.includes(Number(r.idSector)));
        setOrdenesServicio(filtradas);
      })
      .catch(console.error)
      .finally(() => setLoadingOS(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Definición de columnas ───────────────────────────────────────
  const colsReq = [
    { key: "NroRequisicion", label: "Nº Requisición" },
    { key: "Fecha_creacion", label: "Fecha", render: (r) => fmtDate(r.Fecha_creacion) },
    { key: "Concepto", label: "Concepto", render: (r) => r.Concepto || "—" },
    {
      key: "IdEstado", label: "Estado", align: "center",
      render: (r) => <EstadoChip estado={r.IdEstado} mapa={ESTADO_REQ_OC} />,
    },
  ];

  const colsOC = [
    { key: "NroOrdenDeCompra", label: "Nº Orden", render: (r) => r.NroOrdenDeCompra || "—" },
    { key: "Fecha_Creacion", label: "Fecha", render: (r) => fmtDate(r.Fecha_Creacion) },
    { key: "Concepto", label: "Concepto", render: (r) => r.Concepto || "—" },
    {
      key: "IdEstado", label: "Estado", align: "center",
      render: (r) => <EstadoChip estado={r.IdEstado} mapa={ESTADO_REQ_OC} />,
    },
  ];

  const colsOS = [
    { key: "idOS", label: "Nº OS" },
    { key: "FechaCreacion", label: "Fecha", render: (r) => fmtDate(r.FechaCreacion) },
    { key: "fechaEntrega", label: "F. Entrega", render: (r) => fmtDate(r.fechaEntrega) },
    {
      key: "estadoOS", label: "Estado", align: "center",
      render: (r) => <EstadoChip estado={r.estadoOS} mapa={ESTADO_OS} />,
    },
  ];

  return (
    <div>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <PendingList
            title="Requisiciones"
            icon={EqualizerIcon}
            color="warning"
            columns={colsReq}
            rows={requisiciones}
            loading={loadingReq}
          />
        </GridItem>

        <GridItem xs={12} sm={12} md={12}>
          <PendingList
            title="Órdenes de Compra"
            icon={ListAltOutlinedIcon}
            color="info"
            columns={colsOC}
            rows={ordenesCompra}
            loading={loadingOC}
          />
        </GridItem>

        <GridItem xs={12} sm={12} md={12}>
          <PendingList
            title="Órdenes de Servicio"
            icon={PermDataSettingIcon}
            color="success"
            columns={colsOS}
            rows={ordenesServicio}
            loading={loadingOS}
          />
        </GridItem>
      </GridContainer>
    </div>
  );
}
